import { Inviter, Invitation, SessionState, UserAgent, Session, InvitationAcceptOptions } from 'sip.js';
import { SessionDescriptionHandler } from 'sip.js/lib/platform/web';
import { LogModule, LogService } from '../services/log.service';

export enum CallDirection {
  Inbound = 'Inbound',
  Outbound = 'Outbound',
}

// Accepts a *single* unfolded header line, e.g.
// P-Asserted-Identity: "Chris" <tel:07748153275;phone-context=chrisdev.com>
// P-Asserted-Identity: <sips:alice@example.com>
// P-Asserted-Identity: <sip:bob@corp.local;user=phone>
export interface ParsedPAI {
  scheme: 'sip' | 'sips' | 'tel';
  uri: string; // Full URI part inside <...> without angle brackets
  displayName?: string; // If present in "..."
  userPart?: string; // before '@' for sip/sips, or the tel number for tel:
  hostPart?: string; // after '@' for sip/sips
  phoneContext?: string; // for tel:;phone-context=...
}

const PAI_SIP_REGEX =
  /^P-Asserted-Identity:\s*(?:"([^"]*)"\s*)?<sips?:([^>]+)>/i;
const PAI_TEL_REGEX =
  /^P-Asserted-Identity:\s*(?:"([^"]*)"\s*)?<tel:([^>;]+)(?:;phone-context=([^;>\s]+))?/i;

// Folded (continued) headers join lines starting with SP/HTAB per RFC 5322
function unfoldHeader(raw: string): string {
  return raw.replace(/\r?\n[ \t]+/g, ' ');
}

export function parsePAssertedIdentity(
  rawHeaderLine: string
): ParsedPAI | null {
  const line = unfoldHeader(rawHeaderLine).trim();

  // tel: form
  {
    const m = line.match(PAI_TEL_REGEX);
    if (m) {
      const display = m[1]?.trim() || undefined;
      const number = m[2]?.trim();
      const phoneCtx = m[3]?.trim() || undefined;
      if (!number) return null;
      return {
        scheme: 'tel',
        uri: phoneCtx
          ? `tel:${number};phone-context=${phoneCtx}`
          : `tel:${number}`,
        displayName: display,
        userPart: number,
        phoneContext: phoneCtx,
      };
    }
  }

  // sip/sips form
  {
    const m = line.match(PAI_SIP_REGEX);
    if (m) {
      const display = m[1]?.trim() || undefined;
      const uri = m[2]?.trim(); // everything after sip: or sips: up to '>'
      if (!uri) return null;
      // Try to split user@host for convenience (best-effort)
      const at = uri.indexOf('@');
      const userPart = at > -1 ? uri.slice(0, at) : undefined;
      const hostPart = at > -1 ? uri.slice(at + 1) : undefined;

      const scheme = line.toLowerCase().includes('<sips:') ? 'sips' : 'sip';
      return {
        scheme,
        uri: `${scheme}:${uri}`,
        displayName: display,
        userPart,
        hostPart,
      };
    }
  }

  return null;
}

function getPAssertedIdentityHeader(session: Session): string | undefined {
  // Incoming INVITE -> Invitation
  const inviteReq = (session as Invitation)?.request;
  if (inviteReq && typeof inviteReq.getHeader === 'function') {
    const header = inviteReq.getHeader('P-Asserted-Identity');
    if (header) return `P-Asserted-Identity: ${header}`;
  }

  // Some stacks/cases keep raw headers on message objects; best-effort fallback:
  const anySession: any = session as any;
  const rawFromAltPaths =
    anySession?.incomingInviteRequest?.message?.headers?.[
      'p-asserted-identity'
    ]?.[0]?.raw ??
    anySession?.outgoingInviteRequest?.message?.headers?.[
      'p-asserted-identity'
    ]?.[0]?.raw;

  if (rawFromAltPaths) return rawFromAltPaths as string;

  return undefined;
}

export class EveCall {
  public readonly id = crypto.randomUUID();
  public direction: CallDirection;
  public remoteUser: string;
  public startTime: Date;
  public isOnHold: boolean = false;
  public duration: number = 0; // In seconds
  public isInConference: boolean = false;
  public conferenceId: string | null = null;

  // New: session state tracking. Initialized with the session's current state.
  public session: Inviter | Invitation;
  private durationInterval: any = null;

  public isMuted: boolean = false;

  // --- new for DTMF feedback ---
  private audioContext: AudioContext;
  private dtmfFrequencies: Record<string, [number, number]> = {
    '1': [697, 1209],
    '2': [697, 1336],
    '3': [697, 1477],
    A: [697, 1633],
    '4': [770, 1209],
    '5': [770, 1336],
    '6': [770, 1477],
    B: [770, 1633],
    '7': [852, 1209],
    '8': [852, 1336],
    '9': [852, 1477],
    C: [852, 1633],
    '*': [941, 1209],
    '0': [941, 1336],
    '#': [941, 1477],
    D: [941, 1633],
  };

  constructor(session: Inviter | Invitation, private logService: LogService) {
    this.session = session;
    this.direction =
      session instanceof Invitation
        ? CallDirection.Inbound
        : CallDirection.Outbound;

    this.remoteUser = this.extractRemoteUser(session);
    // const paiHeader = getPAssertedIdentityHeader(session);

    // if (paiHeader) {
    //   // const parsed = parsePAssertedIdentity(paiHeader);
    //   // if (parsed) {
    //   //   // Choose what you want to display/store.
    //   //   // Common choices:
    //   //   //  - tel with phone-context -> number@context
    //   //   //  - sip/sips -> full URI
    //   //   //  - prefer displayName if present, else number/URI
    //   //   debugger;
    //   //   if (parsed.scheme === 'tel') {
    //   //     const pretty = parsed.phoneContext
    //   //       ? `${parsed.userPart}@${parsed.phoneContext}`
    //   //       : parsed.userPart ?? parsed.uri;
    //   //     this.remoteUser = parsed.displayName || pretty || '';
    //   //   } else {
    //   //     this.remoteUser = parsed.displayName || parsed.uri || '';
    //   //   }
    //   // } else {
    //   //   // PAI present but couldn't parse; fall back
    //   //   this.remoteUser = session.remoteIdentity.uri.toString();
    //   // }
    // } else {
    //   // No PAI; fall back
    //   this.remoteUser = session.remoteIdentity.uri.toString();
    // }

    // this.remoteUser = session.remoteIdentity.uri.toString();
    this.startTime = new Date();

    // Start tracking call duration.
    this.durationInterval = setInterval(() => {
      this.duration = Math.floor(
        (new Date().getTime() - this.startTime.getTime()) / 1000
      );
    }, 1000);

    // init WebAudio
    this.audioContext = new AudioContext();
  }

  async acceptCall(options?: InvitationAcceptOptions): Promise<void> {
    if (!(this.session instanceof Invitation)) return;
    try {
      await this.session.accept(options);
      console.log(`📞 Call with ${this.remoteUser} accepted.`);
      // Optionally, update the session state manually if needed.
      // This may already be updated by SIP.js.
    } catch (error: any) {
      this.logService.logError(
        `❌ Failed to accept call: ${error.message}`,
        LogModule.SIP
      );
      throw error;
    }
  }

  async rejectCall(): Promise<void> {
    if (!(this.session instanceof Invitation)) return;
    try {
      await this.session.reject();
      console.log(`❌ Call with ${this.remoteUser} rejected.`);
    } catch (error: any) {
      this.logService.logError(
        `❌ Failed to reject call: ${error.message}`,
        LogModule.SIP
      );
      throw error;
    }
  }

  async holdCallImmediate(): Promise<void> {
    if (!this.session) {
      console.warn('⚠️ No active call to hold.');
      return;
    }
    try {
      const options = {
        sessionDescriptionHandlerModifiers: [this.holdModifier.bind(this)],
        requestDelegate: {
          onAccept: () => {
            this.isOnHold = true;
            console.log(`✅ Call with ${this.remoteUser} held.`);
          },
          onReject: () => {
            console.warn(`❌ Call hold was rejected by ${this.remoteUser}.`);
            this.isOnHold = false;
          },
        },
      };
      this.isOnHold = true;
      await this.session.invite(options);
      console.log(`⏸️ Call with ${this.remoteUser} put on hold.`);
    } catch (error: any) {
      this.logService.logError(
        `❌ Failed to hold call: ${error.message}`,
        LogModule.SIP
      );
      throw error;
    }
  }

  async unholdCallImmediate(): Promise<void> {
    if (!this.session) {
      console.warn('⚠️ No active call to resume.');
      return;
    }
    const options = {
      sessionDescriptionHandlerModifiers: [],
      requestDelegate: {
        onAccept: () => {
          this.isOnHold = false;
          console.log(`✅ Call with ${this.remoteUser} resumed.`);
        },
        onReject: () => {
          console.warn(`❌ Call resume was rejected by ${this.remoteUser}.`);
          this.isOnHold = true;
        },
      },
    };
    try {
      await this.session.invite(options);
      console.log(`▶️ Call with ${this.remoteUser} resumed.`);
    } catch (error: any) {
      this.logService.logError(
        `❌ Failed to unhold call: ${error.message}`,
        LogModule.SIP
      );
      throw error;
    }
  }

  /**
   * Hang up this call.
   * - If we’re still in “Establishing”, send a CANCEL
   * - Otherwise send a BYE
   */
  async endCall(): Promise<void> {
    if (!this.session) return;

    try {
      // Outgoing call not yet answered → cancel
      if (
        this.session instanceof Inviter &&
        this.session.state === SessionState.Establishing
      ) {
        await this.session.cancel();
        console.log(`❌ Outgoing call to ${this.remoteUser} canceled.`);
      }
      // Already answered (or in early media) → normal hangup
      else {
        await this.session.bye();
        console.log(`🔚 Call with ${this.remoteUser} ended.`);
      }

      clearInterval(this.durationInterval);
    } catch (error: any) {
      this.logService.logError(
        `❌ Failed to end call: ${error.message}`,
        LogModule.SIP
      );
      throw error;
    }
  }

  async sendDTMF(digit: string): Promise<void> {
    console.log(`🔢 Attempting to send RTP DTMF: '${digit}'...`);
    if (!this.session) {
      console.warn('⚠️ No active call session found. Cannot send DTMF.');
      return;
    }

    // play local tone
    this.playLocalDTMF(digit);

    const sessionDescriptionHandler = this.session
      .sessionDescriptionHandler as SessionDescriptionHandler;
    if (!sessionDescriptionHandler) {
      console.warn('⚠️ No SessionDescriptionHandler available for DTMF.');
      return;
    }
    const peerConnection = sessionDescriptionHandler.peerConnection;
    if (!peerConnection) {
      console.warn('⚠️ No PeerConnection found.');
      return;
    }
    let dtmfSender = peerConnection
      .getSenders()
      .find((sender) => sender.track?.kind === 'audio')?.dtmf;
    if (!dtmfSender) {
      console.log('❌ No DTMF sender found.');
      return;
    }
    try {
      dtmfSender.insertDTMF(digit, 800, 400);
      console.log(`✅ RTP DTMF '${digit}' sent successfully.`);
    } catch (err: any) {
      this.logService.logError(
        `❌ Failed to send DTMF '${digit}': ${err.message}`,
        LogModule.SIP
      );
      throw err;
    }
  }

  async transferTo(target: string, realm: string): Promise<void> {
    if (!this.session) {
      return Promise.reject(new Error('No session to transfer'));
    }

    const sipTarget = target.includes('@')
      ? `sip:${target}`
      : `sip:${target}@${realm}`;

    const targetURI = UserAgent.makeURI(sipTarget);
    if (!targetURI) {
      return Promise.reject(new Error('Invalid target URI'));
    }

    try {
      await this.session.refer(targetURI);
      console.log(
        `🔁 Call with ${this.remoteUser} transferred to ${sipTarget}`
      );
    } catch (err: any) {
      this.logService.logError(
        `❌ Transfer failed for ${this.remoteUser}: ${err.message}`,
        LogModule.SIP
      );
      throw err;
    }
  }

  // Optional: per-call queue so re-INVITEs for the same call never overlap
  private reinviteChains = new WeakMap<EveCall, Promise<any>>();
  private enqueueFor(fn: () => Promise<void>) {
    const prev = this.reinviteChains.get(this) ?? Promise.resolve();
    const next = prev.then(fn, fn);
    this.reinviteChains.set(
      this,
      next.catch(() => {})
    );
    return next;
  }

  private delay(ms: number) {
    return new Promise((res) => setTimeout(res, ms));
  }

  public async unholdCall(): Promise<void> {
    if (!this.isOnHold) {
      return; // Already unheld
    }
    return this.enqueueFor(async () => {
      for (let attempt = 0; attempt < 6; attempt++) {
        try {
          await this.unholdCallImmediate();
          return;
        } catch (e: any) {
          const msg = e?.message ?? '';
          if (/RequestPendingError|Reinvite in progress/i.test(msg)) {
            await this.delay(150 * (attempt + 1)); // backoff: 150ms, 300ms, ...
            continue;
          }
          throw e;
        }
      }
      throw new Error('Unhold failed: re-INVITE stayed pending after retries.');
    });
  }

  public async holdCall(): Promise<void> {
    if (this.isOnHold) {
      return; // Already on hold
    }
    return this.enqueueFor(async () => {
      for (let attempt = 0; attempt < 6; attempt++) {
        try {
          await this.holdCallImmediate();
          return;
        } catch (e: any) {
          const msg = e?.message ?? '';
          if (/RequestPendingError|Reinvite in progress/i.test(msg)) {
            await this.delay(150 * (attempt + 1)); // backoff: 150ms, 300ms, ...
            continue;
          }
          throw e;
        }
      }
      throw new Error('hold failed: re-INVITE stayed pending after retries.');
    });
  }

  private async holdModifier(description: any): Promise<any> {
    let sdp = description.sdp;
    sdp = sdp.replace(/a=sendrecv\r\n/g, 'a=sendonly\r\n');
    description.sdp = sdp;
    return description;
  }

  private playLocalDTMF(digit: string, duration = 200) {
    const freqs = this.dtmfFrequencies[digit];
    if (!freqs) return;
    const now = this.audioContext.currentTime;
    const gain = this.audioContext.createGain();
    gain.gain.setValueAtTime(0.3, now);
    gain.connect(this.audioContext.destination);

    const [f1, f2] = freqs;
    const o1 = this.audioContext.createOscillator();
    const o2 = this.audioContext.createOscillator();
    o1.frequency.value = f1;
    o2.frequency.value = f2;
    o1.connect(gain);
    o2.connect(gain);
    o1.start(now);
    o2.start(now);
    o1.stop(now + duration / 1000);
    o2.stop(now + duration / 1000);
  }

  async mute(): Promise<void> {
    if (this.isMuted) {
      console.log(`🔇 Call with ${this.remoteUser} is already muted`);
      return;
    }

    try {
      // ✅ FIX 1: Mute outgoing audio (what you send to them)
      const sender = this.getAudioSender();
      if (sender?.track) {
        console.log(
          `🔍 Before mute sender - track enabled: ${sender.track.enabled}`
        );
        sender.track.enabled = false;
        console.log(
          `🔍 After mute sender - track enabled: ${sender.track.enabled}`
        );
      }

      // ✅ FIX 2: Mute incoming audio (what you receive from them)
      // const receiver = this.getAudioReceiver();
      // if (receiver?.track) {
      //   console.log(
      //     `🔍 Before mute receiver - track enabled: ${receiver.track.enabled}`
      //   );
      //   receiver.track.enabled = false;
      //   console.log(
      //     `🔍 After mute receiver - track enabled: ${receiver.track.enabled}`
      //   );
      // }

      this.isMuted = true;
      console.log(`🔇 Call with ${this.remoteUser} muted (both directions)`);

      await this.logService.logInfo(
        `Call with ${this.remoteUser} has been muted`,
        LogModule.SIP
      );
    } catch (error: any) {
      await this.logService.logError(
        `Failed to mute call with ${this.remoteUser}: ${error.message}`,
        LogModule.SIP
      );
      throw error;
    }
  }

  async unmute(): Promise<void> {
    if (!this.isMuted) {
      console.log(`🔊 Call with ${this.remoteUser} is already unmuted`);
      return;
    }

    try {
      // ✅ FIX 1: Unmute outgoing audio (what you send to them)
      const sender = this.getAudioSender();
      if (sender?.track) {
        sender.track.enabled = true;
      }

      // // ✅ FIX 2: Unmute incoming audio (what you receive from them)
      // const receiver = this.getAudioReceiver();
      // if (receiver?.track) {
      //   receiver.track.enabled = true;
      // }

      this.isMuted = false;
      console.log(`🔊 Call with ${this.remoteUser} unmuted (both directions)`);

      await this.logService.logInfo(
        `Call with ${this.remoteUser} has been unmuted`,
        LogModule.SIP
      );
    } catch (error: any) {
      await this.logService.logError(
        `Failed to unmute call with ${this.remoteUser}: ${error.message}`,
        LogModule.SIP
      );
      throw error;
    }
  }

  // ✅ ADD: Toggle method for convenience
  async toggleMute(): Promise<void> {
    if (this.isMuted) {
      await this.unmute();
    } else {
      await this.mute();
    }
  }

  // ✅ ADD: Method to get the audio receiver
  private getAudioReceiver(): RTCRtpReceiver | null {
    if (!this.session) {
      console.warn('No session available');
      return null;
    }

    const sessionDescriptionHandler = this.session
      .sessionDescriptionHandler as SessionDescriptionHandler;

    if (!sessionDescriptionHandler?.peerConnection) {
      console.warn('No peer connection available');
      return null;
    }

    const peerConnection = sessionDescriptionHandler.peerConnection;
    const audioReceiver = peerConnection
      .getReceivers()
      .find((receiver) => receiver.track?.kind === 'audio');

    if (!audioReceiver) {
      console.warn('No audio receiver found');
      return null;
    }

    return audioReceiver;
  }

  // ✅ UPDATE: Improved getAudioSender method with better error handling
  private getAudioSender(): RTCRtpSender | null {
    if (!this.session) {
      console.warn('No session available');
      return null;
    }

    const sessionDescriptionHandler = this.session
      .sessionDescriptionHandler as SessionDescriptionHandler;

    if (!sessionDescriptionHandler?.peerConnection) {
      console.warn('No peer connection available');
      return null;
    }

    const peerConnection = sessionDescriptionHandler.peerConnection;
    const audioSender = peerConnection
      .getSenders()
      .find((sender) => sender.track?.kind === 'audio');

    if (!audioSender) {
      console.warn('No audio sender found');
      return null;
    }

    return audioSender;
  }

  // ✅ UPDATE: Better validation for mute capability
  public canBeMuted(): boolean {
    if (this.session?.state !== SessionState.Established) {
      return false;
    }

    const sender = this.getAudioSender();
    return sender !== null && sender.track !== null;
  }

   private extractRemoteUser(session: Inviter | Invitation): string {
    const paiHeader = getPAssertedIdentityHeader(session);

    if (paiHeader) {
      const parsed = parsePAssertedIdentity(paiHeader);
      if (parsed) {
        if (parsed.scheme === 'tel') {
          if (parsed.displayName) {
            let displayName = parsed.displayName;
            if (parsed.userPart && parsed.displayName.toLocaleLowerCase() !== parsed.userPart.toLocaleLowerCase()) {
              displayName += ` (${parsed.userPart})`;
            }
            return displayName;
          } else {
            return parsed.userPart || session.remoteIdentity.uri.toString();
          }
        } else {
          let displayName = parsed.displayName || parsed.userPart || session.remoteIdentity.uri.toString();
          if (displayName && parsed.userPart && parsed.hostPart) {
            displayName += ` (${parsed.userPart}@${parsed.hostPart})`;
          }
          return displayName;
        }
      }
    }

    // No PAI or couldn't parse; fall back to remote identity
    return session.remoteIdentity.uri.toString();
  }
}
